import React, { useMemo, useCallback, useState, useRef, createContext, useContext, useEffect } from 'react'
import {
  Separator,
  BillboardButton,
  DialogOverlay,
  DialogOverlayFooter,
  DialogOverlayActions,
  Button,
  Paragraph,
  useOverlay,
  Divider,
  Expander,
  ExpanderHeader,
  ExpanderContent,
  usePageWidth,
} from '@fs/zion-ui'
import { Trans, useTranslation } from 'react-i18next'
import ZionForm, { useZionForm, FormAutosuggest, useWatch } from '@fs/zion-form'
import { ContentAdd } from '@fs/zion-icon'
import { css } from '@emotion/core'
import { useFeatureFlag } from '@fs/zion-flags'
import zionDebug from '@fs/zion-debug'
import {
  getMostRecentFieldValue,
  getNestedElementById,
  getNestedOrFloatingElementById,
  getPersonName,
  useSlsDispatchContext,
  useSlsHighlightsContext,
  useSlsStateContext,
} from '@fs/zion-sls'
import { useFamilyReviewDispatchContext, useFamilyReviewStateContext, STEPS } from '../../providers/FamilyReviewContext'
import HighlightButton from '../highlightButton/HighlightButton'
import {
  getAllPersons,
  getEventTypeSuggestions,
  getPlaceFieldValues,
  getDateFieldValues,
  getPrimaryOrFirstPerson,
} from '../../utils/utils'
import { showEventsHighlights } from '../../utils/highlightUtils'
import { translateEventFieldChips } from '../../../../utils/translationUtils'
import { useR2Highlights } from '../../../../providers/r2Provider/R2Context'
import { saveFamilyReviewEditEvent } from '../../utils/saveFunctions'
import FieldSelectorOverlay from '../../../../components/generic/panels/shared/fieldChipSelector/FieldSelectorOverlay'
import { addPlaceAndDateFieldsToFormDefaults, getNextUuid } from '../../../../../shared/utils/utils'
import ShareableField from '../shareableField/ShareableField'
import useTreatedFieldChips from '../../../indexing/hooks/useTreatedFieldChips'

const debug = zionDebug('rip:EditEventForm')

// we need to remove the bottom margin because the Autosuggest has a margin where we want a button
// we need to remove the top margin to remove a compensating negative margin the Place and Date Suggest components have.
const noMarginsCss = css`
  & div {
    margin-bottom: 0px;
    margin-top: 0px;
  }
`

// To avoid a prop drilling mess
const PersonAutosuggestContext = createContext(null)

// Task: use the `selectedItem prop on the Autosuggest
const EditEventForm = ({
  reviewPersonEventsOverlay,
  currentTask,
  editHighlightsOverlay,
  setSaveUpdateCalled,
  handleEditEventClose,
  isPrimaryEvent,
}) => {
  const h = useSlsHighlightsContext()
  const recordId = currentTask.id
  const addOrRemoveEventFieldsOverlay = useOverlay()
  const saveChangesFlag = useFeatureFlag('rip_sharedImageViewer_saveChanges')
  const [t] = useTranslation()
  const atPageWidth = usePageWidth()
  const isSmallScreen = atPageWidth({ default: true, md: false })
  const dispatch = useFamilyReviewDispatchContext()
  const state = useFamilyReviewStateContext()
  const zionSLSState = useSlsStateContext()
  const zionSlsDispatch = useSlsDispatchContext()
  const { displayHighlights } = useR2Highlights()
  const [selectedTempChips, setSelectedTempChips] = useState([])
  const [lockedTempChips, setLockedTempChips] = useState([])

  const eventTypeChangeOverlay = useOverlay()
  const [temporaryEventType, setTemporaryEventType] = useState(null)
  const [unlinkingStates, setUnlinkingStates] = useState({})

  const primaryPerson = currentTask.subElements.find((person) => person.primary)
  const currentEvent = useMemo(() => {
    return getNestedElementById(currentTask.subElements, state.selectedEventId)
  }, [currentTask.subElements, state.selectedEventId])

  const currentEventPrimaryPerson = currentTask.subElements.find((person) => person?.id === currentEvent?.personId)
  const selectablePersonElements = useMemo(() => {
    return isPrimaryEvent
      ? getAllPersons(currentTask).filter((person) => person.id !== currentEventPrimaryPerson?.id)
      : getAllPersons(currentTask)
  }, [currentTask, isPrimaryEvent, currentEventPrimaryPerson?.id])

  const [personIdsInUse, setPersonIdsInUse] = useState(() => {
    return selectablePersonElements
      .filter((person) => {
        return currentEvent?.superElements.some((se) => se.id === person.id)
      })
      .map((person) => person.id)
  })
  const [eventPersonChipDisabled, setEventPersonChipDisabled] = useState(
    personIdsInUse.length >= selectablePersonElements.length
  )

  const [autosuggestStateArray, setAutosuggestStateArray] = useState(() => {
    // These represent the autosuggest elements on the form, which can be empty
    return personIdsInUse.map((id) => {
      return {
        id: `person-${getNextUuid()}`,
        personId: id,
        displayLabel: getPersonName({
          person: selectablePersonElements.find((person) => person.id === id),
          justOneName: true,
        }),
      }
    })
  })

  const personSuggestions = useMemo(() => {
    return selectablePersonElements
      .filter((person) => !personIdsInUse.includes(person.id))
      .map((person) => ({
        primaryText: getPersonName({ person, justOneName: true }),
        key: person.id,
        personId: person.id,
      }))
  }, [personIdsInUse, selectablePersonElements])

  const [currentEventType, setCurrentEventType] = useState({
    eventType: currentEvent?.eventType,
    displayLabel: currentEvent?.displayLabel,
  })

  // event type fields
  const typeSuggestions = useMemo(() => {
    const primaryOrFirstPerson = getPrimaryOrFirstPerson(currentTask)
    const primaryPersonSelected = autosuggestStateArray.find(
      (autosuggest) => autosuggest.personId === primaryOrFirstPerson.id
    )
    const primaryEventType = state.stepThree.eventCards
      .map((cardState) => getNestedElementById(currentTask?.subElements, cardState.eventId))
      .filter((event) => event?.primary)[0]?.eventType
    return getEventTypeSuggestions({
      types: zionSLSState.types,
      t,
      isPrimary: isPrimaryEvent,
      primaryEventType: primaryPersonSelected ? primaryEventType : null,
    })
  }, [zionSLSState.types, t, isPrimaryEvent, state.stepThree.eventCards, currentTask, autosuggestStateArray])

  const [fieldValues, setFieldValues] = useState([
    ...getPlaceFieldValues(currentEvent),
    ...getDateFieldValues(currentEvent),
  ])

  const { treatedFieldChipData } = useTreatedFieldChips(
    zionSLSState.types,
    fieldValues,
    selectedTempChips,
    lockedTempChips,
    translateEventFieldChips
  )

  function getDefaultValues() {
    let eventTypeFormDefault = {}
    // once we have the current event, set the default values of the form which should be reinstantiated because
    // of the key where EditEvent is used in FamilyReview
    if (currentTask && currentEvent) {
      // initial person
      // event type
      const eventTypeFieldElement = currentEvent.subElements?.find((se) => se.fieldType === 'EVENT_TYPE')
      const eventTypePrimaryText = getMostRecentFieldValue(eventTypeFieldElement) || currentEvent?.displayLabel
      const eventTypeKey = currentEvent?.eventType || getMostRecentFieldValue(eventTypeFieldElement)
      eventTypeFormDefault = {
        [currentEvent.id]: { primaryText: eventTypePrimaryText, key: eventTypeKey },
      }

      // persons form state (potentially empty entry for all possible persons)
      const personDefaultValues = {}
      autosuggestStateArray.forEach((autosuggest) => {
        personDefaultValues[autosuggest.id] = {
          primaryText: autosuggest.displayLabel,
          key: autosuggest.personId,
        }
      })

      const fieldDefaultValues = {}
      currentEvent &&
        addPlaceAndDateFieldsToFormDefaults(
          fieldDefaultValues,
          currentEvent?.subElements?.flatMap((se) => se?.subElements).filter((se) => se?.elementType === 'FIELD') || []
        )

      return Object.assign(eventTypeFormDefault, personDefaultValues, fieldDefaultValues)
    }
    return {}
  }

  const methods = useZionForm({
    mode: 'all',
    defaultValues: getDefaultValues(),
    resolver: (values) => {
      let errors = {}
      methods.clearErrors()
      const valuesArray = Object.entries(values)
      const personValues = valuesArray.filter(([key]) => key.includes('person'))
      const requirePersonChecks = !isPrimaryEvent
      const containsNoPerson = ([, value]) => !value || !value?.key
      let personCheckFails = false

      if (requirePersonChecks) {
        personCheckFails = personValues.every(containsNoPerson)
        if (personCheckFails) {
          personValues.forEach(([key]) => {
            errors[key] = { type: 'required', message: t('FormValidation.notEmpty', 'Field cannot be empty.') }
          })
        }
      }

      const eventTypeCheckFails = !valuesArray.find(([key]) => key === currentEvent.id)[1]?.key
      if (eventTypeCheckFails) {
        errors = { ...errors, [currentEvent.id]: { type: 'required', message: 'Event Type is required' } }
      }

      return { values, errors }
    },
  })

  const [type] = useWatch({
    name: [currentEvent?.id],
    control: methods.control,
  })

  const handleSubmit = useCallback(
    async (values) => {
      await saveFamilyReviewEditEvent({
        eventId: currentEvent.id,
        dirtyFields: methods.formState.dirtyFields,
        highlights: [...h.getHighlights().mainEditHighlights, ...h.getHighlights().splitScreenEditHighlights],
        partitionKey: currentTask.partitionKey,
        saveChangesFlag,
        values,
        userId: state.userId,
        zionSLSState,
        zionSlsDispatch,
        unlinkingStates,
        isPrimaryEvent,
        primaryPerson,
        currentTask,
        recordId,
        handleError: (error) => dispatch({ type: 'SET_ERROR', value: error }),
        handleSuccess: () => {
          setSaveUpdateCalled(true)
          handleEditEventClose(currentEvent.id, [
            ...h.getHighlights().mainEditHighlights,
            ...h.getHighlights().splitScreenEditHighlights,
          ])
          if (state.currentStep === STEPS.STEP_FOUR) reviewPersonEventsOverlay.open()
        },
      })
    },
    [
      h,
      currentEvent.id,
      methods.formState.dirtyFields,
      setSaveUpdateCalled,
      handleEditEventClose,
      reviewPersonEventsOverlay,
      saveChangesFlag,
      state.currentStep,
      state.userId,
      zionSLSState,
      zionSlsDispatch,
      unlinkingStates,
      isPrimaryEvent,
      primaryPerson,
      currentTask,
      recordId,
      dispatch,
    ]
  )

  const handleHighlightButtonClicked = (id) => {
    if (id) {
      dispatch({ type: 'SET_ACTIVE_FIELD_ID', value: id })
      h.setDisplayHighlights({
        mainPartitionKey: currentTask.partitionKey,
        splitScreenPartitionKey: state.splitScreenPartitionKey,
        setMainZoom: true,
        setSplitScreenZoom: Boolean(state.splitScreenPartitionKey),
        attachFilter: (hl) => hl.fieldId === id,
        mainZoomFilter: (hl) => hl.fieldId === id,
        splitScreenZoomFilter: (hl) => hl.fieldId === id,
        isSmallScreen,
      })
    }
  }

  const handlePersonChipSelections = useCallback(
    (chips) => {
      // build new autosuggests if there are new event-person chips
      const numberOfNewAutosuggests = chips.filter((chip) => chip.key === 'new-event-person').length
      const newAutosuggests = Array.from({ length: numberOfNewAutosuggests }, () => {
        const newPersonAutosuggestFormKey = `person-${getNextUuid()}`
        // The form value needs to be explicitly set so as to avoid a downshift error
        methods.setValue(newPersonAutosuggestFormKey, { key: '', primaryText: '' }, { shouldDirty: true })
        return {
          id: newPersonAutosuggestFormKey,
          personId: '',
          displayLabel: '',
        }
      })
      // remove autosuggests by id if they aren't present in these chips, and add in the new ones
      const autosuggestsToRemove = autosuggestStateArray.filter((autosuggest) =>
        chips.every((chip) => chip.id !== autosuggest.id)
      )
      autosuggestsToRemove.forEach((autosuggest) => {
        // update person suggestions
        if (autosuggest.personId) {
          setPersonIdsInUse((prevPersonIdsInUse) => prevPersonIdsInUse.filter((id) => id !== autosuggest.personId))
        }
        methods.unregister(autosuggest.id)
      })
      setAutosuggestStateArray((prev) => {
        const newArray = [
          ...prev.filter((autosuggest) => !autosuggestsToRemove.includes(autosuggest)),
          ...newAutosuggests,
        ]
        if (newArray.length === 0) {
          const newPersonAutosuggestFormKey = `person-${getNextUuid()}`
          // The form value needs to be explicitly set so as to avoid a downshift error
          methods.setValue(newPersonAutosuggestFormKey, { key: '', primaryText: '' }, { shouldDirty: false })
          return [
            {
              id: newPersonAutosuggestFormKey,
              personId: '',
              displayLabel: '',
            },
          ]
        }
        return newArray
      })
    },
    [autosuggestStateArray, methods]
  )

  const updateHighlightsForRemovedFields = useCallback(
    (removedIds) => {
      const highlightIdsThatAreShowing = Object.values(displayHighlights.highlights).flatMap((hl) => hl.fieldId)
      const fieldIds = highlightIdsThatAreShowing.filter((id) => !removedIds.includes(id))
      showEventsHighlights({
        h,
        currentTask,
        eventIds: [],
        highlights: [...h.getHighlights().mainEditHighlights, ...h.getHighlights().splitScreenEditHighlights],
        forceFieldIds: fieldIds,
        isSmallScreen,
        splitScreenPartitionKey: state.splitScreenPartitionKey,
      })
    },
    [displayHighlights, h, currentTask, state.splitScreenPartitionKey, isSmallScreen]
  )

  const handleFieldChipSelection = useCallback(
    (chips, lockedChips) => {
      // for each chip that was removed
      const removedIds = fieldValues
        .filter(
          (field) => chips.every((chip) => chip.id !== field.id) && lockedChips.every((chip) => chip.id !== field.id)
        )
        .map((field) => field.id)

      // helper to check if I should delete the field from the form or mark it as removed
      const elementExists = (id) => getNestedElementById(currentTask.subElements, id)

      // remove the field from the form
      removedIds.forEach((id) => {
        if (elementExists(id)) {
          methods.setValue(id, 'REMOVED', { shouldDirty: true })
        } else {
          methods.unregister(id)
          methods.unregister(`${id}-fieldType`)
          methods.unregister(`${id}-superType`)
        }
      })
      updateHighlightsForRemovedFields(removedIds)
      // remove the field from the fieldValues state array
      setFieldValues((prev) => prev.filter((field) => !removedIds.includes(field.id)))

      // for each chip that was added
      const addedChips = chips.filter((chip) => {
        if (chip.key) return chip.key.includes('new-field')
        return false
      })

      // add the field to the fieldValues state array
      const newFieldValues = addedChips.map((chip) => {
        const fieldId = getNextUuid()
        const fieldType = chip.value.split(':')[0]
        const superType = chip.elementtypes[0]
        methods.setValue(fieldId, '', { shouldDirty: true })
        methods.setValue(`${fieldId}-fieldType`, fieldType, { shouldDirty: true })
        methods.setValue(`${fieldId}-superType`, superType, { shouldDirty: true })
        return {
          displayLabel: chip.label,
          fieldType,
          id: fieldId,
          value: '',
          added: true,
        }
      })
      setFieldValues((prev) => [...prev, ...newFieldValues])
    },
    [currentTask, fieldValues, methods, updateHighlightsForRemovedFields]
  )

  const handleChipSelection = useCallback(
    (chips, lockedChips) => {
      handlePersonChipSelections(chips)
      handleFieldChipSelection(chips, lockedChips)
    },
    [handlePersonChipSelections, handleFieldChipSelection]
  )

  const handleChipSelectionChange = (chips, lockedChips) => {
    setSelectedTempChips(chips)
    setLockedTempChips(lockedChips)
    let totalEventPersons = 0
    chips.forEach((chip) => {
      if (chip.label.includes('Event Person')) totalEventPersons++
    })
    if (totalEventPersons === selectablePersonElements.length) setEventPersonChipDisabled(true)
    else setEventPersonChipDisabled(false)
  }

  const onFieldValueChange = useCallback(({ event, index }) => {
    setFieldValues((prev) => {
      const next = [...prev]
      next[index].value = event.target.value
      return next
    })
  }, [])

  const onPersonSuggestionSelected = ({ event, autoSuggestIndex, oldPersonId }) => {
    if (event) {
      const { personId: newPersonId } = event
      // replace the personId in use
      setPersonIdsInUse((prev) => [...prev.filter((id) => id !== oldPersonId), newPersonId])

      // update the autosuggest values
      setAutosuggestStateArray((prev) =>
        prev.map((autosuggest, index) => {
          if (index === autoSuggestIndex)
            return {
              ...autosuggest,
              personId: newPersonId,
              displayLabel: getPersonName({
                person: selectablePersonElements.find((person) => person.id === newPersonId),
                justOneName: true,
              }),
            }
          return autosuggest
        })
      )
    } else {
      // release the personId to be used in future suggestions
      setPersonIdsInUse((prev) => prev.filter((id) => id !== oldPersonId))
      // update the autosuggest values
      setAutosuggestStateArray((prev) => {
        return prev.map((autosuggest, index) => {
          if (index === autoSuggestIndex) return { ...autosuggest, personId: '', displayLabel: '' }
          return autosuggest
        })
      })
    }
  }

  const getSelectedItem = useCallback(
    (autosuggestState) => {
      return {
        key: autosuggestState.personId || '',
        primaryText:
          getPersonName({
            person: selectablePersonElements.find((person) => person.id === autosuggestState.personId),
            justoneName: true,
          }) || '',
      }
    },
    [selectablePersonElements]
  )

  const confirmPrimaryEventTypeChange = useCallback(
    (selectedSuggestion) => {
      if (!selectedSuggestion) return
      if (selectedSuggestion.key === currentEvent?.eventType) return
      // Trigger modal to confirm event type change.
      setTemporaryEventType({
        eventType: selectedSuggestion?.key,
        displayLabel: selectedSuggestion?.primaryText,
      })
      eventTypeChangeOverlay.open()
    },
    [eventTypeChangeOverlay, currentEvent]
  )

  const restorePrevious = () => {
    setCurrentEventType(currentEventType)
    methods.setValue(
      currentEvent?.id,
      {
        key: currentEvent?.eventType,
        primaryText: currentEventType?.displayLabel,
      },
      {
        shouldDirty: true,
      }
    )
  }

  const clickedButton = useRef(false)

  const handleCloseButton = (event) => {
    const name = event?.target?.name
    eventTypeChangeOverlay.close()
    clickedButton.current = name
  }

  const handleClose = () => {
    const buttonName = clickedButton.current
    if (buttonName === 'keep' || !buttonName) {
      restorePrevious()
    }
    clickedButton.current = false
  }

  const currentEventPersonsOnThisRecord = []
  const subElementsOnThisRecord = currentTask.subElements.flatMap((el) => el.id)
  const superPeopleOnThisEvent = currentEvent.superElements?.filter((el) => el.id.includes('1:1'))
  superPeopleOnThisEvent.forEach((person) => {
    if (
      subElementsOnThisRecord.includes(person.id) &&
      !currentEventPersonsOnThisRecord.flatMap((el) => el.id).includes(person.id)
    )
      currentEventPersonsOnThisRecord.push(getNestedElementById(currentTask.subElements, person.id))
  })

  const setUnlinkingState = useCallback((field, value) => {
    setUnlinkingStates((prevStates) => ({
      ...prevStates,
      [field.id]: { ...prevStates[field.id], state: value, type: field.fieldType },
    }))
  }, [])

  const getUnlinkingState = useCallback(
    (field) => {
      return unlinkingStates[field.id]?.state || false
    },
    [unlinkingStates]
  )

  const hasSelectedHighlights = useCallback(
    (fieldId) => {
      return [...h.getHighlights().mainEditHighlights, ...h.getHighlights().splitScreenEditHighlights].find(
        (highlight) => highlight.fieldId === fieldId
      )
    },
    [h]
  )

  debug('EditEventForm state values', {
    'Form values': methods.getValues(),
    selectablePersonElements,
    autosuggestStateArray,
    personIdsInUse,
    personSuggestions,
    fieldValues,
    isPrimaryEvent,
  })

  const fieldHasHighlight = (field) => {
    const fieldId = getMostRecentFieldValue(field) || field.id
    return Boolean([...h.getHighlights().mainEditHighlights].find((highlight) => highlight.fieldId === fieldId))
  }

  useEffect(() => {
    if (type?.key && !typeSuggestions?.find((suggestion) => suggestion.key === type.key)) {
      methods.setValue(currentEvent?.id, { key: '', primaryText: '' })
    }
  }, [typeSuggestions, type, methods, currentEvent?.id])

  const chipData = [
    {
      title: t('family-review.edit-event.fieldSelector.common-fields', 'Most Common Fields'),
      data: [
        ...treatedFieldChipData
          .filter(byMostCommonEventFields)
          .map((chip) => ({ ...chip, key: `new-field:${getNextUuid()}` })),
        {
          label: t('edit-index.chip.event-person', 'Event Person'),
          key: 'new-event-person',
          disabled: eventPersonChipDisabled,
        },
      ],
      key: 'commonFamilyReviewEventFields',
    },
    {
      title: t('edit-index.chips.event-fields.title', 'Event Fields'),
      data: treatedFieldChipData.map((chip) => ({
        ...chip,
        key: `new-field:${getNextUuid()}`,
      })),
      key: 'eventFields',
    },
  ]

  return (
    <ZionForm methods={methods} id="edit-event" onSubmit={handleSubmit}>
      <PersonAutosuggests
        {...{
          autosuggestStateArray,
          getSelectedItem,
          personSuggestions,
          onPersonSuggestionSelected,
          isPrimaryEvent,
          t,
        }}
      />
      <Divider />
      <Separator size="xs" />
      <span css={noMarginsCss}>
        <FormAutosuggest
          data-testid="type-autosuggest"
          clearOnClickAway
          clearable
          id="event-type"
          name={currentEvent?.id}
          label={t('family-review.addEvent.formLabel.type', 'Event Type')}
          placeholder={t('family-review.addEvent.formLabel.type', 'Event Type')}
          suggestions={typeSuggestions}
          onSuggestionSelected={(suggestion) => {
            if (isPrimaryEvent) {
              confirmPrimaryEventTypeChange(suggestion)
            }
          }}
        />
      </span>
      <HighlightButton
        isAdding={
          !fieldHasHighlight(currentEvent.subElements?.find((se) => se.fieldType === 'EVENT_TYPE') || currentEvent)
        }
        editHighlightsOverlay={editHighlightsOverlay}
        handleHighlightButtonClicked={() => handleHighlightButtonClicked(currentEvent?.id)}
      />
      <Separator />
      {fieldValues?.map((field, index) => {
        let disableUnlinking = false
        const superElement = getNestedOrFloatingElementById(
          zionSLSState?.images,
          field.superElements?.find(
            (superEl) => getNestedOrFloatingElementById(zionSLSState?.images, superEl?.id)?.personId === field?.personId
          )?.id
        )

        if (superElement?.superElements?.length > 1) {
          disableUnlinking = true
        } else if (superElement?.elementType === 'DATE' || superElement?.elementType === 'PLACE') {
          const eventElement = getNestedOrFloatingElementById(
            zionSLSState?.images,
            superElement?.superElements?.[0]?.id
          )
          if (eventElement?.superElements?.length > 1) {
            disableUnlinking = true
          }
        }

        return (
          <span key={field.id} css={noMarginsCss}>
            <ShareableField
              field={field}
              unlinkingState={[getUnlinkingState(field), (value) => setUnlinkingState(field, value)]}
              editHighlightsOverlay={editHighlightsOverlay}
              handleHighlightButtonClicked={handleHighlightButtonClicked}
              hasSelectedHighlights={hasSelectedHighlights}
              fieldType="event"
              onFieldValueChange={(e) => onFieldValueChange({ event: e, index })}
              disableUnlinking={disableUnlinking}
            />
          </span>
        )
      })}
      <FieldSelectorOverlay
        lockSharedFields
        title={t('edit-index.add-fields.title', 'Add or Remove Fields')}
        chipData={chipData}
        fieldData={[
          ...fieldValues.map((field) => ({ ...field, label: field.displayLabel })),
          ...autosuggestStateArray.map((autosuggestState) => ({
            ...autosuggestState,
            label: `${t('family-review.addEvent.chip.person', 'Event Person ({personName})', {
              personName: autosuggestState.displayLabel,
            })}`,
          })),
        ]}
        handleSelectionChange={handleChipSelectionChange}
        handleSelection={handleChipSelection}
        overlay={addOrRemoveEventFieldsOverlay}
        unlinkingStates={unlinkingStates}
      />
      <Separator size="xs" />
      <AddOrRemoveFieldsButton
        onClick={() => {
          addOrRemoveEventFieldsOverlay.open()
        }}
      />
      <DialogOverlay
        {...eventTypeChangeOverlay}
        title={t('family-review.editEventForm.warningDialog.title', 'Are you sure?')}
        onClose={handleClose}
      >
        <Trans i18nKey="family-review.editEvent.dialog.changePrimaryEventText">
          <Paragraph>
            The computer identified this as a <strong> {{ cD: currentEventType?.displayLabel }} Record. </strong>
          </Paragraph>
        </Trans>
        <Trans i18nKey="family-review.editEvent.dialog.changePrimaryEventText2">
          <Paragraph>
            By changing the
            <i> Primary Event Type,</i>
            you are indicating that it is a <strong> {{ td: temporaryEventType?.displayLabel }} Record</strong>.
          </Paragraph>
        </Trans>
        <Paragraph>{t('family-review.editEvent.dialog.confirmChange', 'Please confirm this change.')}</Paragraph>
        <DialogOverlayFooter>
          <DialogOverlayActions>
            <Button
              name="change"
              onClick={handleCloseButton}
              data-testid="change-to-button"
              linkName="Edit Event Change Event Type"
            >
              {t('family-review.editEvent.dialog.changeTo', 'Change to')} {temporaryEventType?.displayLabel}
            </Button>
            <Button
              emphasis="high"
              name="keep"
              onClick={handleCloseButton}
              data-testid="keep-as-button"
              linkName="Edit Event Keep Event Type"
            >
              {t('family-review.editEvent.dialog.keepAs', 'Keep as')} {currentEventType?.displayLabel}
            </Button>
          </DialogOverlayActions>
        </DialogOverlayFooter>
      </DialogOverlay>
    </ZionForm>
  )
}

// Helper JSX components

function AddOrRemoveFieldsButton({ onClick }) {
  const [t] = useTranslation()
  return (
    <BillboardButton
      data-testid="add-or-remove-fields"
      Icon={ContentAdd}
      color="yellow"
      emphasis="low"
      onClick={onClick}
      linkName="Edit Event Add or Remove Fields"
    >
      {t('edit-index.add-fields.button', 'Add or Remove Fields')}
    </BillboardButton>
  )
}

// Helper functions

function byMostCommonEventFields(field) {
  const mostCommonEventFields = ['Date', 'City', 'Country', 'Place', 'Providence', 'State', 'Territory']
  return mostCommonEventFields.includes(field.label)
}

function PersonAutosuggests({
  autosuggestStateArray,
  getSelectedItem,
  personSuggestions,
  onPersonSuggestionSelected,
  isPrimaryEvent,
  t,
}) {
  const contextValue = useMemo(
    () => ({
      autosuggestStateArray,
      getSelectedItem,
      personSuggestions,
      onPersonSuggestionSelected,
      isPrimaryEvent,
      t,
    }),
    [autosuggestStateArray, getSelectedItem, personSuggestions, onPersonSuggestionSelected, isPrimaryEvent, t]
  )

  return (
    <PersonAutosuggestContext.Provider value={contextValue}>
      {isPrimaryEvent ? <PrimaryEventPersonAutosuggests /> : <NonPrimaryEventPersonAutosuggests />}
    </PersonAutosuggestContext.Provider>
  )
}

function PrimaryEventPersonAutosuggests() {
  return <PersonAutosuggestExpander />
}

function NonPrimaryEventPersonAutosuggests() {
  return (
    <>
      <PersonAutosuggest index={0} />
      <PersonAutosuggestExpander skipFirstIndex />
    </>
  )
}

function PersonAutosuggestExpander({ skipFirstIndex }) {
  const { autosuggestStateArray, t } = useContext(PersonAutosuggestContext)
  const totalOtherPersons = skipFirstIndex ? autosuggestStateArray.length - 1 : autosuggestStateArray.length
  const headerText = useMemo(() => {
    return totalOtherPersons > 1
      ? t('family-review.addEvent.expanderHeader', '+ {totalOtherPersons} Other People', {
          totalOtherPersons,
        })
      : t('family-review.addEvent.expanderHeader-singular', '+ {otherPerson} Other Person', {
          otherPerson: totalOtherPersons,
        })
  }, [totalOtherPersons, t])

  return totalOtherPersons > 0 ? (
    <Expander>
      <ExpanderHeader>{headerText}</ExpanderHeader>
      <ExpanderContent>
        {autosuggestStateArray.map((item, index) => {
          if (skipFirstIndex && index === 0) return null
          const key = item.id ? `person-autosuggest-${item.id}-${index}` : `person-autosuggest-${index}`
          return <PersonAutosuggest key={key} index={index} />
        })}
      </ExpanderContent>
    </Expander>
  ) : null
}

function PersonAutosuggest({ index }) {
  const { autosuggestStateArray, getSelectedItem, personSuggestions, onPersonSuggestionSelected, t } =
    useContext(PersonAutosuggestContext)
  return (
    <FormAutosuggest
      name={autosuggestStateArray[index].id}
      key={autosuggestStateArray[index].id}
      selectedItem={getSelectedItem(autosuggestStateArray[index])}
      data-testid="person-autosuggest"
      clearOnClickAway
      clearable
      label={t('family-review.addEvent.formLabel.person', 'Event Person')}
      placeholder={t('family-review.addEvent.formLabel.person', 'Event Person')}
      suggestions={personSuggestions}
      onSuggestionSelected={(e) =>
        onPersonSuggestionSelected({
          event: e,
          autoSuggestIndex: index,
          oldPersonId: autosuggestStateArray[index].personId, // could be null
        })
      }
    />
  )
}

export default EditEventForm
